import { Clear, Search } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import {
  ComponentProps,
  HTMLAttributes,
  type ReactNode,
  useMemo,
  useRef,
} from 'react'

import { TPropsWithFieldProps } from '~/components/formikFields/models'
import WrapWithFastField from '~/components/formikFields/wrapWithFastField'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import rem from '~/utils/ui'

export type TFormikLabeledAutocompleteOption = {
  label: string
  value: string
  group?: string
  icon?: ReactNode
  search?: string
}

export type TRenderFormikLabeledAutocompleteOption = (
  props: HTMLAttributes<HTMLLIElement> & { key: string },
  option: TFormikLabeledAutocompleteOption,
) => ReactNode

type TAutocompleteProps = ComponentProps<
  typeof Autocomplete<TFormikLabeledAutocompleteOption, false, boolean, boolean>
>

export type TFormikLabeledAutocompleteCreateOption = {
  icon?: ReactNode
  label: string
  onClick: (search: string) => void
}

const CREATE_OPTION_VALUE = '__create_option__'

const filterAutocompleteOptions =
  createFilterOptions<TFormikLabeledAutocompleteOption>({
    stringify: (option) => option.search ?? option.label,
  })

type TProps = {
  capability?: ECapability
  createOption?: TFormikLabeledAutocompleteCreateOption
  disabled?: boolean
  freeSolo?: boolean
  helperText?: ReactNode
  isLoading?: boolean
  label?: string
  onChange?: () => void
  transformValue?: (value: string) => string
  options: TFormikLabeledAutocompleteOption[]
  placeholder?: string
  required?: boolean
  disableClearable?: boolean
  startIcon?: ReactNode
  sx?: SxProps<Theme>
  withInternalLabel?: boolean
  renderOption?: TRenderFormikLabeledAutocompleteOption
} & TPropsWithFieldProps<string, object> &
  Pick<TAutocompleteProps, 'getOptionKey'>

const FormikLabeledAutoComplete = WrapWithFastField<TProps, string, object>(
  function Component({
    capability,
    createOption,
    disableClearable = false,
    disabled = false,
    fieldProps,
    freeSolo = false,
    getOptionKey = undefined,
    helperText = null,
    isLoading = false,
    label = '',
    onChange: onChangeProps,
    transformValue = undefined,
    options: optionsFromProps,
    placeholder = '',
    renderOption: renderOptionFromProps = undefined,
    required = false,
    startIcon,
    sx = {},
    withInternalLabel = false,
  }) {
    const theme = useTheme()
    const { canEdit } = useCapabilities()

    const { field, form, meta } = fieldProps
    const { isSubmitting, setFieldTouched, setFieldValue } = form
    const { error, touched } = meta
    const { name, onBlur, value } = field

    const options = useMemo(() => {
      const list = [...optionsFromProps]

      if (freeSolo && !list.some((o) => o.value === value)) {
        list.push({ value, label: value })
      }

      return list
    }, [freeSolo, optionsFromProps, value])

    const hasGroups = useMemo(
      () => options.some((option) => !!option.group),
      [options],
    )

    const autocompleteValue = useMemo(
      () => options.find((opt) => opt.value === value) || null,
      [options, value],
    )

    const inputValueRef = useRef('')

    const handleChange = async (
      _: unknown,
      newValue: string | TFormikLabeledAutocompleteOption | null,
    ) => {
      if (
        typeof newValue !== 'string' &&
        newValue?.value === CREATE_OPTION_VALUE
      ) {
        createOption?.onClick(inputValueRef.current.trim())
        return
      }

      const next =
        typeof newValue === 'string' ? newValue : (newValue?.value ?? '')
      const transformed = transformValue ? transformValue(next) : next
      await setFieldValue(name, transformed)
      await setFieldTouched(name, true)
      onChangeProps?.()
    }

    const handleClear = async () => {
      await setFieldValue(name, '')
      await setFieldTouched(name, true)
      onChangeProps?.()
    }

    const isDisabledByCapability = capability && !canEdit(capability)
    const isAutocompleteDisabled =
      disabled || isLoading || isSubmitting || isDisabledByCapability

    const defaultRenderOption: TRenderFormikLabeledAutocompleteOption = (
      { key: optionKey, ...props },
      option,
    ) =>
      !option.value ? null : (
        <Box
          {...props}
          key={optionKey}
          component="li"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: rem(8),
            ...(option.value === CREATE_OPTION_VALUE
              ? {
                  borderTop: 1,
                  borderColor: theme.palette.divider,
                  marginTop: rem(4),
                }
              : {}),
          }}
        >
          {option.icon}
          {option.label}
        </Box>
      )

    const resolvedRenderOption = renderOptionFromProps ?? defaultRenderOption

    return (
      <FormControl error={!!touched && !!error} fullWidth sx={sx}>
        {!!label && !withInternalLabel && (
          <FormLabel
            required={required}
            sx={{
              '&, &.Mui-focused': {
                color: theme.palette.mainFont,
                fontSize: rem(14),
                fontWeight: 500,
                marginBottom: rem(8),
              },
            }}
          >
            {label}
          </FormLabel>
        )}
        <Autocomplete<TFormikLabeledAutocompleteOption, false, boolean, boolean>
          autoSelect={freeSolo}
          {...(disableClearable ? { disableClearable: true } : {})}
          disabled={isAutocompleteDisabled}
          filterOptions={(opts, params) => {
            const filtered = filterAutocompleteOptions(opts, params)
            const search = params.inputValue.trim()
            inputValueRef.current = search

            if (createOption) {
              filtered.push({
                icon: createOption.icon,
                label: search ? `Add "${search}"` : createOption.label,
                value: CREATE_OPTION_VALUE,
              })
            }

            return filtered
          }}
          freeSolo={freeSolo}
          getOptionKey={getOptionKey}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          groupBy={hasGroups ? (option) => option.group || '' : undefined}
          isOptionEqualToValue={(option, selected) =>
            !!selected && option.value === selected.value
          }
          ListboxProps={{ sx: { maxHeight: rem(350) } }}
          loading={isLoading}
          onBlur={onBlur}
          onChange={handleChange}
          options={options}
          renderGroup={
            hasGroups
              ? (params) => (
                  <Box component="li" key={params.key}>
                    <Typography
                      sx={{
                        position: 'sticky',
                        top: -rem(8),
                        pt: rem(8),
                        pb: rem(8),
                        px: 2,
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: theme.typography.fontWeightBold,
                      }}
                    >
                      {params.group}
                    </Typography>
                    <Box component="ul" sx={{ padding: 0, margin: 0 }}>
                      {params.children}
                    </Box>
                  </Box>
                )
              : undefined
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              error={!!touched && !!error}
              label={withInternalLabel ? label : undefined}
              placeholder={placeholder}
              required={withInternalLabel ? required : undefined}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    {isLoading ? (
                      <CircularProgress color="inherit" size={rem(16)} />
                    ) : (
                      (startIcon ?? (
                        <Search
                          sx={{
                            color: theme.palette.secondary1,
                            fontSize: rem(20),
                          }}
                        />
                      ))
                    )}
                  </InputAdornment>
                ),
                endAdornment: disableClearable ? undefined : (
                  <InputAdornment position="end">
                    {!!autocompleteValue?.value &&
                      !isLoading &&
                      !isAutocompleteDisabled && (
                        <Clear
                          onClick={handleClear}
                          onMouseDown={(event) => event.preventDefault()}
                          sx={{
                            cursor: 'pointer',
                            height: rem(16),
                            width: rem(16),
                            color: theme.palette.secondary1,
                          }}
                        />
                      )}
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                ...(withInternalLabel
                  ? {
                      '& .MuiInputBase-root': {
                        borderRadius: rem(4),
                      },
                    }
                  : {
                      height: rem(36),
                      '& .MuiInputBase-root': {
                        borderRadius: rem(8),
                        height: rem(36),
                        paddingBlock: 0,
                      },
                    }),
              }}
            />
          )}
          renderOption={
            resolvedRenderOption as NonNullable<
              TAutocompleteProps['renderOption']
            >
          }
          slotProps={{
            paper: {
              sx: {
                '&.MuiPaper-root': {
                  borderRadius: rem(8),
                  marginTop: rem(4),
                },
              },
            },
          }}
          sx={{
            '&&& .MuiAutocomplete-inputRoot': {
              paddingInline: rem(10),
            },
          }}
          value={autocompleteValue}
        />
        {(!!touched && !!error) || Boolean(helperText) ? (
          <FormHelperText error={!!touched && !!error}>
            {(touched && error) || helperText}
          </FormHelperText>
        ) : null}
      </FormControl>
    )
  },
)

export default FormikLabeledAutoComplete
